import { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import type { HelpHubBranding, HelpHubQuestion } from "@/types/help-hub/help-hub";
import { HubSection } from "./HubSection";

/** Questions shown before the "show everything" control appears. */
const VISIBLE_CAP = 9;

interface HubFaqSectionProps {
  questions: HelpHubQuestion[];
  branding: HelpHubBranding;
  /** Opens the Genie with this question already asked. */
  onAsk: (question: string) => void;
}

/**
 * "What people ask us" — the band that makes a Genie Page worth landing on.
 *
 * Every question here is a real string already stored against a published
 * guide (`metadata.answers`, written when the knowledge base read the source).
 * Nothing is generated for this page and no answer text is stored on it:
 * tapping a question opens the Genie and asks it, so the answer comes from the
 * live knowledge base and can never sit here going quietly out of date.
 */
export const HubFaqSection = ({ questions, branding, onAsk }: HubFaqSectionProps) => {
  const [expanded, setExpanded] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, HelpHubQuestion[]>();
    for (const question of questions) {
      const key = question.topic || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(question);
    }
    return [...map.entries()];
  }, [questions]);

  if (questions.length === 0) return null;

  // Grouping only earns its keep when there is more than one real topic.
  const useGroups = grouped.length > 1 && grouped.every(([topic]) => topic !== "");
  const visible = expanded ? questions : questions.slice(0, VISIBLE_CAP);

  // One question per line, top to bottom — a list you can run your eye down,
  // not a mosaic you have to read cell by cell.
  const row = (question: HelpHubQuestion) => (
    <button
      key={question.id}
      type="button"
      onClick={() => onAsk(question.question)}
      className="group flex min-h-[44px] w-full items-center justify-between gap-4 px-4 py-3.5 text-left transition-colors hover:bg-genie-background sm:px-5"
    >
      <span className="text-[14.5px] leading-snug text-genie-text-primary">
        {question.question}
      </span>
      <ArrowUpRight
        className="h-4 w-4 shrink-0 opacity-60 transition-opacity group-hover:opacity-100"
        style={{ color: branding.primaryColor }}
        strokeWidth={2.25}
      />
    </button>
  );

  const listOf = (items: HelpHubQuestion[]) => (
    <div
      className="divide-y divide-genie-border overflow-hidden rounded-2xl border bg-white"
      style={{ borderColor: `${branding.primaryColor}26` }}
    >
      {items.map(row)}
    </div>
  );

  return (
    <HubSection
      id="questions"
      title="What people ask us"
      subtitle="Tap one and the Genie answers it, right now."
      genieIcon="chat-check"
      branding={branding}
    >
      {useGroups ? (
        <div className="space-y-6">
          {grouped.map(([topic, topicQuestions]) => (
            <div key={topic}>
              <div className="mb-2.5 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wide text-genie-text-muted">
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: branding.primaryColor }}
                />
                {topic}
              </div>
              {listOf(expanded ? topicQuestions : topicQuestions.slice(0, 3))}
            </div>
          ))}
        </div>
      ) : (
        listOf(visible)
      )}

      {questions.length > VISIBLE_CAP && (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="mt-4 text-[13.5px] font-semibold underline-offset-2 hover:underline"
          style={{ color: branding.primaryColor }}
        >
          {expanded ? "Show fewer questions" : `Show all ${questions.length} questions`}
        </button>
      )}
    </HubSection>
  );
};

export default HubFaqSection;
